/**
 * ResourceRequestsTab
 *
 * World-class table-based view for inbound resource requests.
 * Table for scanning/triage → expandable row detail → Plan Drawer.
 * Evo Design System — semantic tokens only, no hardcoded colours.
 */

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import { formatDistanceToNow, parseISO, format } from 'date-fns';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  useRequestPriorityThresholds,
  computeRequestPriority,
} from '@/hooks/useRequestPriorityThresholds';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Search,
  Timer,
  CalendarDays,
  Tag,
  CheckCircle2,
  XCircle,
  Trash2,
  ChevronRight,
  Inbox,
  Filter,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ExternalLink,
  MapPin,
  MessageSquare,
  Download,
  RefreshCw,
  Settings2,
  GripVertical,
  RotateCcw,
  Lock,
  ChevronDown,
  PieChart,
  Eye,
} from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { ResourceRequest, PlanScenario, PlanPreviewScenario, ProposedBookingSlot, RequestPriority, AcceptedRequestBooking } from '@/types/resourceRequest';
import { Absence, Resource, Booking, WorkingHoursProfile } from '@/types';
import { generatePlanScenarios, monthsForRequest } from '@/lib/resourceRequestPlanner';
import {
  blockingErrorsOnly,
  computePlanDateBounds,
  validatePlanSlots,
  warningsOnly,
  type UnavailableDay,
} from '@/lib/slotEditValidation';
import { mapApiBookingToBooking } from '@/hooks/use-date-range-bookings';
import { ResourceRequestPlanDrawer } from './ResourceRequestPlanDrawer';
import { RequestedSkillBadges, skillsSectionLabel } from './RequestedSkillBadges';
import { toast } from 'sonner';
import { absencesApi, demandsApi, bookingsApi, projectsApi, resourcesApi, ApiError, type Booking as ApiBooking } from '@/services/api';
import { mockAbsences, mockBankHolidays } from '@/lib/mock-data';
import { getProjectColor } from '@/lib/utils/calendar';
import type { Demand, AllocateDailyBatchItem } from '@/types/demand';
import { demandsToResourceRequests, findDemandLineByMonth, isProjectRemoved } from '@/lib/demandAdapter';
import {
  applyManualBookedHours,
  manualBookedHoursForRequest,
  NO_MANUAL_HOURS,
  type ManualBookedHours,
} from '@/lib/manualDemandHours';
import { OPEN_DEMAND_COUNT_EVENT } from '@/lib/openDemandEvents';
import {
  TablePagination,
  DEFAULT_PAGE_SIZE,
  paginate,
} from '@/components/TablePagination';
import {
  alternatingRowStyle,
  alternatingRowHoverHandlers,
  TABLE_CONTAINER_CLASS,
  TABLE_HEADER_ROW_CLASS,
  TABLE_HEAD_CLASS,
} from '@/lib/table-styles';
import { getStartDateProximity } from '@/lib/startDateProximity';
import { useCompany } from '@/contexts/CompanyContext';
import { useDataMode } from '@/contexts/DataModeContext';
import { useScheduler } from '@/contexts/SchedulerContext';
import { DEMAND_SOURCE } from '@/lib/demand-booking';

import { getDemoResourceRequests } from '@/data/demoDemands';
import { Loader2, BarChart3 } from 'lucide-react';
import {
  budgetHealthAccent,
  findRoleBudget,
  parseMonthlyHours,
  useProjectBudget,
} from '@/hooks/useProjectBudget';

// ── Types ─────────────────────────────────────────────────────────────────────

/** Multi-select status filter; an empty selection means every status except accepted. */
type StatusFilter   = ResourceRequest['status'][];
type PriorityFilter = 'all' | RequestPriority;
type SortField      = 'priority' | 'project' | 'role' | 'hours' | 'startDate' | 'received' | 'status';
type SortDir        = 'asc' | 'desc' | 'none';

// ── Column configuration ───────────────────────────────────────────────────────

export type RRColumnKey = 'priority' | 'project' | 'role' | 'hours' | 'startDate' | 'status';
type RRColumnCategory = 'Core' | 'Details';

interface RRColumnDef {
  key: RRColumnKey;
  label: string;
  description: string;
  category: RRColumnCategory;
  defaultVisible: boolean;
  locked?: boolean;
  sortField?: SortField;
}

const RR_ALL_COLUMNS: RRColumnDef[] = [
  { key: 'priority',  label: 'Priority',   description: 'Request urgency and priority level',    category: 'Core',    defaultVisible: true, locked: true,  sortField: 'priority'  },
  { key: 'project',   label: 'Project',    description: 'Project name and code',                 category: 'Core',    defaultVisible: true, locked: true,  sortField: 'project'   },
  { key: 'role',      label: 'Role',       description: 'Required role for the request',          category: 'Details', defaultVisible: true,               sortField: 'role'      },
  { key: 'hours',     label: 'Hours',      description: 'Total hours requested',                 category: 'Details', defaultVisible: true,               sortField: 'hours'     },
  { key: 'startDate', label: 'Start Date', description: 'Requested start date',                  category: 'Core',    defaultVisible: true,               sortField: 'startDate' },
  { key: 'status',    label: 'Status',     description: 'Current request status',                category: 'Core',    defaultVisible: true,               sortField: 'status'    },
];

const RR_DEFAULT_VISIBLE = new Set<RRColumnKey>(RR_ALL_COLUMNS.filter(c => c.defaultVisible).map(c => c.key));
const RR_DEFAULT_ORDER   = RR_ALL_COLUMNS.map(c => c.key);

/** Status options offered by the filter, in the order they are listed. */
const RR_STATUS_FILTER_OPTIONS: ResourceRequest['status'][] = [
  'pending', 'plan-ready', 'partially-allocated', 'declined', 'project-removed', 'accepted',
];

/** Statuses pre-selected whenever the screen mounts (menu click or page reload). */
const RR_DEFAULT_STATUS_FILTER: ResourceRequest['status'][] = [
  'pending', 'partially-allocated', 'plan-ready', 'project-removed',
];

function isDefaultStatusFilter(selected: StatusFilter) {
  return selected.length === RR_DEFAULT_STATUS_FILTER.length
    && RR_DEFAULT_STATUS_FILTER.every(s => selected.includes(s));
}

/** Statuses where the requested start date is still actionable, so its proximity is shown. */
const RR_PROXIMITY_STATUSES = new Set<ResourceRequest['status']>(['pending', 'partially-allocated']);

/** Uniform sizing for the row quick-action buttons so every label fits on one line. */
const RR_ROW_ACTION_CLASS = 'h-8 w-[132px] justify-center gap-1.5 px-3 text-xs';

// ── Config (EDS tokens — no hardcoded colours) ─────────────────────────────────

const STATUS_CONFIG: Record<ResourceRequest['status'], {
  label: string;
  accent: 'teal' | 'warning' | 'info' | 'destructive';
  icon: React.ReactNode;
}> = {
  'pending':    { label: 'Pending',    accent: 'warning',     icon: <Timer       className="h-3 w-3" /> },
  'plan-ready': { label: 'Plan Ready', accent: 'info',        icon: <CheckCircle2 className="h-3 w-3" /> },
  'partially-allocated': { label: 'Partially Allocated', accent: 'info', icon: <PieChart className="h-3 w-3" /> },
  'accepted':   { label: 'Accepted',   accent: 'teal',        icon: <CheckCircle2 className="h-3 w-3" /> },
  'declined':   { label: 'Declined',   accent: 'destructive', icon: <XCircle     className="h-3 w-3" /> },
  'project-removed': { label: 'Project Removed', accent: 'destructive', icon: <Trash2 className="h-3 w-3" /> },
};

/** Statuses with nothing left to plan, so the planning actions are withheld. */
const RR_TERMINAL_STATUSES = new Set<ResourceRequest['status']>(['declined', 'project-removed']);

const PRIORITY_CONFIG: Record<RequestPriority, {
  label: string;
  dotAccent: string;           // inline style key into EDS accent palette
  badgeAccent: 'destructive' | 'warning' | 'info' | 'teal';
  rowAccent: string;           // subtle left-border on expanded row
  order: number;
}> = {
  critical: { label: 'Critical', dotAccent: 'accent-destructive', badgeAccent: 'destructive', rowAccent: 'border-l-[var(--accent-destructive-500)]', order: 0 },
  high:     { label: 'High',     dotAccent: 'accent-warning',     badgeAccent: 'warning',     rowAccent: 'border-l-[var(--accent-warning-500)]',     order: 1 },
  medium:   { label: 'Medium',   dotAccent: 'accent-info',        badgeAccent: 'info',        rowAccent: 'border-l-[var(--accent-info-500)]',        order: 2 },
  low:      { label: 'Low',      dotAccent: 'accent-teal',        badgeAccent: 'teal',        rowAccent: 'border-l-[var(--accent-teal-500)]',        order: 3 },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso: string) {
  return format(parseISO(iso), 'd MMM yy');
}

function hoursLabel(h: number) {
  if (h % 8 === 0) return `${h}h / ${h / 8}d`;
  return `${h}h`;
}



function sortRequests(
  requests: ResourceRequest[],
  field: SortField,
  dir: SortDir,
): ResourceRequest[] {
  if (dir === 'none') return requests;
  const mul = dir === 'asc' ? 1 : -1;
  return [...requests].sort((a, b) => {
    switch (field) {
      case 'priority':   return mul * (PRIORITY_CONFIG[a.priority].order - PRIORITY_CONFIG[b.priority].order);
      case 'project':    return mul * a.projectName.localeCompare(b.projectName);
      case 'role':       return mul * a.role.localeCompare(b.role);
      case 'hours':      return mul * (a.totalHoursRequested - b.totalHoursRequested);
      case 'startDate':  return mul * (parseISO(a.requestedStartDate).getTime() - parseISO(b.requestedStartDate).getTime());
      case 'received':   return mul * (parseISO(a.receivedAt).getTime() - parseISO(b.receivedAt).getTime());
      case 'status': {
        const so: Record<ResourceRequest['status'], number> = { pending: 0, 'plan-ready': 1, 'partially-allocated': 2, accepted: 3, declined: 4, 'project-removed': 5 };
        return mul * (so[a.status] - so[b.status]);
      }
      default: return 0;
    }
  });
}

// ── SortableHeader ─────────────────────────────────────────────────────────────

interface SortableHeadProps {
  field: SortField;
  label: string;
  currentField: SortField;
  currentDir: SortDir;
  onSort: (field: SortField) => void;
  className?: string;
}

function SortableHead({ field, label, currentField, currentDir, onSort, className }: SortableHeadProps) {
  const active = currentField === field && currentDir !== 'none';
  return (
    <TableHead className={cn('cursor-pointer select-none group', TABLE_HEAD_CLASS, className)} onClick={() => onSort(field)}>
      <div className="flex items-center gap-1">
        <span className={cn(
          'transition-colors',
          active ? 'text-foreground font-semibold' : 'text-muted-foreground group-hover:text-foreground',
        )}>
          {label}
        </span>
        {active ? (
          currentDir === 'asc'
            ? <ArrowUp   className="h-3 w-3 text-primary shrink-0" />
            : <ArrowDown className="h-3 w-3 text-primary shrink-0" />
        ) : (
          <ArrowUpDown className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-60 transition-opacity shrink-0" />
        )}
      </div>
    </TableHead>
  );
}

// ── Sortable column row (drag-and-drop) for column configurator ───────────────

interface RRSortableColumnRowProps {
  colDef: RRColumnDef;
  isVisible: boolean;
  onToggle: (key: RRColumnKey) => void;
}

function RRSortableColumnRow({ colDef, isVisible, onToggle }: RRSortableColumnRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: colDef.key });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    opacity: isDragging ? 0.85 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-3 px-4 py-3.5 rounded-xl border transition-colors duration-150',
        isDragging
          ? 'bg-accent border-primary/40 shadow-lg cursor-grabbing'
          : isVisible && !colDef.locked
            ? 'bg-card border-border hover:border-border/80 hover:bg-accent/30'
            : isVisible && colDef.locked
              ? 'bg-muted/20 border-border/40'
              : 'bg-muted/10 border-border/30',
      )}
    >
      {/* Drag handle */}
      <button
        className={cn(
          'shrink-0 text-muted-foreground/30 transition-colors touch-none',
          !colDef.locked && 'hover:text-muted-foreground cursor-grab active:cursor-grabbing',
          colDef.locked && 'cursor-default opacity-30',
        )}
        {...(!colDef.locked ? { ...attributes, ...listeners } : {})}
        aria-label={`Drag to reorder ${colDef.label}`}
        tabIndex={colDef.locked ? -1 : 0}
      >
        <GripVertical className="h-4 w-4" />
      </button>

      {/* Text */}
      <div className="flex-1 min-w-0">
        <p className={cn('text-sm font-medium leading-snug', !isVisible ? 'text-muted-foreground' : 'text-foreground')}>
          {colDef.label}
        </p>
        <p className="text-xs text-muted-foreground/60 mt-0.5 truncate leading-tight">
          {colDef.description}
        </p>
      </div>

      {/* Right-side controls */}
      <div className="shrink-0 flex items-center gap-3 ml-auto">
        <div className="w-16 flex justify-end">
          <Badge
            variant="accent"
            accent={colDef.category === 'Core' ? 'nile' : 'teal'}
            className="text-[10px] px-2 py-0 h-5 font-medium"
          >
            {colDef.category}
          </Badge>
        </div>
        <div className="w-9 flex items-center justify-center">
          {colDef.locked ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="text-muted-foreground/40 cursor-default">
                  <Lock className="h-3.5 w-3.5" />
                </div>
              </TooltipTrigger>
              <TooltipContent side="left">
                <p className="text-xs">Always visible — required column</p>
              </TooltipContent>
            </Tooltip>
          ) : (
            <Switch
              checked={isVisible}
              onCheckedChange={() => onToggle(colDef.key)}
              aria-label={`${isVisible ? 'Hide' : 'Show'} ${colDef.label} column`}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Monthly hour bars (compact, inside expanded row) ─────────────────────────

const MAX_BAR_HEIGHT_PX = 64;
const MIN_BAR_HEIGHT_PX = 10;
/** Shortest booked fill that can still legibly hold its hours label. */
const MIN_LABEL_FILL_PX = 14;

/** Trim floating point noise so 18.500000001h renders as 18.5h. */
function fmtHours(hours: number): string {
  return `${Math.round(hours * 10) / 10}h`;
}

interface MonthlyBar {
  month:   string;
  label:   string;
  planned: number;
  booked:  number;
}

/**
 * Planned-vs-booked bars per month: the planned bar height is proportional to
 * the month's total planned hours, and the dark fill inside it is the share of
 * those hours already booked (mirroring the budget context progress bar).
 * Overflows horizontally into a scroll area when a request spans many months.
 */
function MonthlyHourBars({ bars }: { bars: MonthlyBar[] }) {
  if (!bars.length) return null;
  const max = Math.max(...bars.map(b => Math.max(b.planned, b.booked)));
  return (
    <div className="overflow-x-auto pb-1">
      <div className="flex items-end gap-2 w-max">
        {bars.map(({ month, label, planned, booked }) => {
          const total      = Math.max(planned, booked);
          const heightPx   = max > 0
            ? Math.max(MIN_BAR_HEIGHT_PX, Math.round((total / max) * MAX_BAR_HEIGHT_PX))
            : MIN_BAR_HEIGHT_PX;
          const bookedPct  = total > 0 ? Math.min(100, (booked / total) * 100) : 0;
          const fillPx     = (bookedPct / 100) * heightPx;
          const showFillLabel = booked > 0 && booked < total && fillPx >= MIN_LABEL_FILL_PX;
          return (
            <TooltipProvider key={month} delayDuration={100}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex flex-col items-center gap-1 cursor-default shrink-0">
                    <span className="text-[10px] font-bold text-foreground/70 tabular-nums">
                      {fmtHours(total)}
                    </span>
                    <div
                      className="w-10 rounded-sm bg-primary/25 hover:bg-primary/40 transition-colors flex flex-col justify-end overflow-hidden"
                      style={{ height: `${heightPx}px` }}
                    >
                      {bookedPct > 0 && (
                        <div
                          className="w-full bg-primary rounded-sm transition-all flex items-center justify-center"
                          style={{ height: `${bookedPct}%` }}
                        >
                          {showFillLabel && (
                            <span className="text-[9px] font-bold leading-none tabular-nums text-primary-foreground">
                              {fmtHours(booked)}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <span className="text-[10px] text-muted-foreground">{label}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs">
                  <div className="font-semibold">{label}</div>
                  <div>Planned: {fmtHours(total)}</div>
                  <div>Booked: {fmtHours(booked)} ({Math.round(bookedPct)}%)</div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Merge the request's outstanding monthly spread with what the accepted plan
 * has already booked. `monthlyHourHints` only covers hours still to allocate,
 * so a month's planned total is its outstanding hours plus its booked hours.
 */
function buildMonthlyBars(
  planned:   Record<string, number>,
  allocated: Record<string, number>,
): MonthlyBar[] {
  const months = Array.from(new Set([...Object.keys(planned), ...Object.keys(allocated)])).sort();
  return months.map(month => {
    const [yr, mo] = month.split('-').map(Number);
    const booked   = allocated[month] ?? 0;
    return {
      month,
      label:   new Date(yr, mo - 1).toLocaleDateString('en-GB', { month: 'short' }),
      planned: (planned[month] ?? 0) + booked,
      booked,
    };
  });
}

// ── Expanded row detail ────────────────────────────────────────────────────────

// ── Budget health colour helpers (EDS semantic) ──────────────────────────────

const HEALTH_COLORS: Record<'teal' | 'warning' | 'destructive', {
  progress: string;
  text: string;
}> = {
  teal:        { progress: 'bg-[var(--accent-teal-500)]',        text: 'text-[var(--accent-teal-700)]' },
  warning:     { progress: 'bg-[var(--accent-warning-500)]',     text: 'text-[var(--accent-warning-700)]' },
  destructive: { progress: 'bg-destructive',                     text: 'text-destructive' },
};

// ── Lightweight budget summary for expanded row ──────────────────────────────

interface BudgetContextBriefProps {
  request: ResourceRequest;
}

/**
 * Budget summary for an open-demand row, computed entirely from the planner:
 * Budget    = the original requested hours for this demand,
 * Consumed  = hours booked by the planner (the accepted plan),
 * Remaining = still-outstanding hours.
 * No EBProjects budget lookup is used — these are the request's own numbers.
 */
function BudgetContextBrief({ request }: BudgetContextBriefProps) {
  const consumedHours = request.allocatedHours ?? 0;
  const budgetHours =
    request.originalHoursRequested ?? request.totalHoursRequested + consumedHours;
  const remainingHours = Math.max(0, budgetHours - consumedHours);
  const percentUsed = budgetHours > 0 ? (consumedHours / budgetHours) * 100 : 0;

  const health = budgetHealthAccent(percentUsed);
  const colors = HEALTH_COLORS[health];

  return (
    <div className="mt-4">
      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
        <BarChart3 className="h-3 w-3" /> Budget Context — {request.role}
      </p>
      <div className="rounded-lg border border-border/50 bg-muted/20 px-3 py-2 space-y-2">
        <div className="grid grid-cols-3 gap-2 text-center">
          <div>
            <div className="text-[9px] text-muted-foreground">Budget</div>
            <div className="text-xs font-bold tabular-nums">{budgetHours}h</div>
          </div>
          <div>
            <div className="text-[9px] text-muted-foreground">Consumed</div>
            <div className="text-xs font-bold tabular-nums">{consumedHours}h</div>
          </div>
          <div>
            <div className="text-[9px] text-muted-foreground">Remaining</div>
            <div className={cn('text-xs font-bold tabular-nums', colors.text)}>{remainingHours}h</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className={cn('h-full rounded-full transition-all', colors.progress)}
              style={{ width: `${Math.min(100, percentUsed)}%` }}
            />
          </div>
          <span className={cn('text-[10px] font-bold tabular-nums', colors.text)}>
            {Math.round(percentUsed)}%
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Expanded row detail ────────────────────────────────────────────────────────

interface ExpandedDetailProps {
  request:        ResourceRequest;
  onViewPlan:     () => void;
  onDecline:      () => void;
  colSpan?:       number;
}

function ExpandedDetail({ request, onViewPlan, onDecline, colSpan = 8 }: ExpandedDetailProps) {
  const priorityCfg = PRIORITY_CONFIG[request.priority];

  // One combined spread: every month the request plans for, with the share of
  // each month already booked by the accepted plan filled in.
  const allocatedHours = request.allocatedHours ?? 0;
  const monthlyBars    = useMemo(
    () => buildMonthlyBars(request.monthlyHourHints ?? {}, request.monthlyAllocatedHours ?? {}),
    [request.monthlyHourHints, request.monthlyAllocatedHours],
  );

  return (
    <TableRow className="hover:bg-transparent border-b-0">
      <TableCell colSpan={colSpan} className="py-0 px-0">
        <div className={cn(
          'mx-4 mb-3 rounded-lg border-l-4 border border-border/50 bg-muted/15 overflow-hidden',
          priorityCfg.rowAccent,
        )}>

          {/* ── Body: 2-col layout ── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 divide-y md:divide-y-0 md:divide-x divide-border/40">

            {/* Left col — metadata */}
            <div className="px-5 py-4 space-y-4">

              {/* Skills */}
              <div>
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <Tag className="h-3 w-3" /> {skillsSectionLabel(request.skillsDetailed)}
                </p>
                {request.skills.length > 0
                  ? <RequestedSkillBadges skills={request.skills} skillsDetailed={request.skillsDetailed} />
                  : <span className="text-xs text-muted-foreground italic">No skills specified</span>
                }
              </div>

              {/* Source + Location row */}
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                <div>
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1 flex items-center gap-1.5">
                    <ExternalLink className="h-3 w-3" /> Requested by
                  </p>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-foreground font-medium">{request.requestedByName}</span>
                  </div>
                </div>

                {request.location && (
                  <div>
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1 flex items-center gap-1.5">
                      <MapPin className="h-3 w-3" /> Location
                    </p>
                    <span className="text-xs text-foreground/80">{request.location}</span>
                  </div>
                )}
              </div>

              {/* Notes */}
              {request.notes && (
                <div>
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1 flex items-center gap-1.5">
                    <MessageSquare className="h-3 w-3" /> Notes from requester
                  </p>
                  <p className="text-xs text-muted-foreground leading-relaxed italic border-l-2 border-border pl-2.5">
                    "{request.notes}"
                  </p>
                </div>
              )}
            </div>

            {/* Right col — monthly spread + budget context */}
            <div className="px-5 py-4">
              <div className="flex items-center justify-between gap-2 mb-3">
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                  <CalendarDays className="h-3 w-3" /> Monthly Allocation (planned vs booked)
                </p>
                {monthlyBars.length > 0 && (
                  <div className="flex items-center gap-3 text-[10px] text-muted-foreground shrink-0">
                    <span className="flex items-center gap-1">
                      <span className="h-2 w-2 rounded-sm bg-primary/25" /> Planned
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="h-2 w-2 rounded-sm bg-primary" /> Booked
                    </span>
                  </div>
                )}
              </div>
              {monthlyBars.length > 0
                ? <MonthlyHourBars bars={monthlyBars} />
                : (
                  <div className="flex items-start gap-2.5 text-xs text-muted-foreground bg-muted/40 rounded-lg px-3 py-3 border border-dashed border-border/60">
                    <CalendarDays className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    <span className="leading-relaxed">
                      {allocatedHours > 0
                        ? `${allocatedHours}h booked from the accepted plan.`
                        : 'No monthly breakdown provided. The planning engine will distribute hours optimally based on resource availability.'}
                    </span>
                  </div>
                )
              }

              {/* Budget Context */}
              <BudgetContextBrief request={request} />
            </div>
          </div>

          {/* ── Footer action strip — partially allocated requests act from the row ── */}
          {!RR_TERMINAL_STATUSES.has(request.status) && request.status !== 'partially-allocated' && (
            <div className="flex items-center gap-2 px-5 py-2.5 bg-muted/20 border-t border-border/40">
              {request.status === 'plan-ready' && (
                <Button size="sm" className="gap-1.5 text-xs h-7" onClick={onViewPlan}>
                  View Plan
                </Button>
              )}
              {request.status === 'accepted' && (
                <Button size="sm" variant="outline" className="gap-1.5 text-xs h-7" onClick={onViewPlan}>
                  <CheckCircle2 className="h-3 w-3" />
                  View Accepted Plan
                </Button>
              )}
              {(request.status === 'pending' || request.status === 'plan-ready') && (
                <Button
                  type="button"
                  size="sm"
                  variant="destructive"
                  className="h-7 ml-auto"
                  onClick={onDecline}
                >
                  <XCircle className="h-4 w-4 mr-1.5" />
                  Decline request
                </Button>
              )}
            </div>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

// ── Empty state ────────────────────────────────────────────────────────────────

function EmptyState({ filtered, onClear, colSpan = 8 }: { filtered: boolean; onClear: () => void; colSpan?: number }) {
  return (
    <TableRow className="hover:bg-transparent">
      <TableCell colSpan={colSpan}>
        <div className="flex flex-col items-center justify-center py-20 text-center px-8">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-muted/60 mb-4 border border-border/50">
            {filtered
              ? <Filter className="h-7 w-7 text-muted-foreground/50" />
              : <Inbox   className="h-7 w-7 text-muted-foreground/50" />
            }
          </div>
          <p className="text-sm font-semibold text-foreground">
            {filtered ? 'No matching requests' : 'No resource requests yet'}
          </p>
          <p className="text-xs text-muted-foreground mt-1.5 max-w-xs leading-relaxed">
            {filtered
              ? 'Clear your filters to see all requests.'
              : 'Requests from project managers in connected systems will appear here.'}
          </p>
          {filtered && (
            <Button variant="outline" size="sm" className="mt-4 gap-1.5 text-xs" onClick={onClear}>
              <Filter className="h-3.5 w-3.5" />
              Clear filters
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

/** Resource details a booking row shows, resolved from the id when unknown. */
interface ResolvedBookingResource {
  name:      string;
  role?:     string;
  photoUrl?: string;
}

/** Unknown resources are resolved a few at a time rather than all at once. */
const RESOURCE_LOOKUP_BATCH_SIZE = 5;

export interface ResourceRequestsTabProps {
  resources:             Resource[];
  existingBookings:      Booking[];
  /** Callback to notify the parent of the current demand count */
  onDemandCountChange?:  (count: number) => void;
  globalWorkingProfile:  WorkingHoursProfile;
  businessHoursStart:    number;
  businessHoursEnd:      number;
  /** Called when the user wants to preview a plan scenario on the main calendar */
  onPreviewOnCalendar?:  (preview: PlanPreviewScenario) => void;
  /** When set, auto-opens the plan drawer for this request id (used when returning from calendar preview) */
  initialOpenRequestId?: string;
  /** Called once the initialOpenRequestId has been consumed so the parent can clear it */
  onInitialOpenRequestIdConsumed?: () => void;
}

export function ResourceRequestsTab({ resources, existingBookings, onDemandCountChange, globalWorkingProfile, businessHoursStart, businessHoursEnd, onPreviewOnCalendar, initialOpenRequestId, onInitialOpenRequestIdConsumed }: ResourceRequestsTabProps) {

  // ── Priority thresholds (configurable in Settings → Open Demand) ──────────
  const thresholds = useRequestPriorityThresholds();
  const { currentCompany } = useCompany();
  const { isDemo } = useDataMode();
  const isDemoMode = isDemo('calendar');
  const { settings, createBatchBookings } = useScheduler();


  // ── Request data (fetched from Demands API in live mode, hardcoded in demo) ─
  const [requests, setRequests] = useState<ResourceRequest[]>([]);
  const [rawDemands, setRawDemands] = useState<Demand[]>([]);
  const [loadingState, setLoadingState] = useState<'loading' | 'loaded' | 'error'>('loading');

  /**
   * Reloads the demands and returns the refreshed requests, so a caller that has
   * just changed a demand (allocating it, declining it) can pick its own request
   * back up with the server's new hours and status rather than a stale snapshot.
   */
  const fetchDemands = useCallback(async (
    options?: { keepVisible?: boolean },
  ): Promise<ResourceRequest[]> => {
    // `keepVisible` reloads in place for the Refresh button: the table stays on
    // screen instead of being replaced by the loading spinner or error state.
    const keepVisible = options?.keepVisible === true;
    if (isDemoMode) {
      const demoRequests = getDemoResourceRequests();
      setRequests(demoRequests);
      setRawDemands([]);
      setLoadingState('loaded');
      return demoRequests;
    }
    if (!currentCompany?.id) {
      setRequests([]);
      setRawDemands([]);
      setLoadingState('loaded');
      return [];
    }
    if (!keepVisible) setLoadingState('loading');
    try {
      const demands = await demandsApi.getByCompany(currentCompany.id);
      const freshRequests = demandsToResourceRequests(demands);
      setRawDemands(demands);
      setRequests(freshRequests);
      setLoadingState('loaded');
      return freshRequests;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch demands';
      toast.error(message);
      if (!keepVisible) setLoadingState('error');
      return [];
    }
  }, [currentCompany?.id, isDemoMode]);

  // ── Manual refresh (requests only — no other screen data is reloaded) ──────
  const [isRefreshing, setIsRefreshing] = useState(false);
  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await fetchDemands({ keepVisible: true });
    } finally {
      setIsRefreshing(false);
    }
  }, [fetchDemands, isRefreshing]);

  useEffect(() => {
    if (isDemoMode) {
      setRequests(getDemoResourceRequests());
      setRawDemands([]);
      setLoadingState('loaded');
      return;
    }
    let cancelled = false;
    if (!currentCompany?.id) {
      setRequests([]);
      setRawDemands([]);
      setLoadingState('loaded');
      return;
    }
    setLoadingState('loading');
    demandsApi.getByCompany(currentCompany.id).then(demands => {
      if (cancelled) return;
      setRawDemands(demands);
      setRequests(demandsToResourceRequests(demands));
      setLoadingState('loaded');
    }).catch(err => {
      if (cancelled) return;
      const message = err instanceof Error ? err.message : 'Failed to fetch demands';
      toast.error(message);
      setLoadingState('error');
    });
    return () => { cancelled = true; };
  }, [currentCompany?.id, isDemoMode]);

  // Notify parent of demand count changes, and broadcast the number of requests
  // still awaiting action so the Open Demand nav badge stays in step with the
  // list (accepted / declined requests no longer count).
  useEffect(() => {
    onDemandCountChange?.(requests.filter(r => r.status === 'pending').length);
    const openCount = requests.filter(r => r.status !== 'accepted' && !RR_TERMINAL_STATUSES.has(r.status)).length;
    window.dispatchEvent(new CustomEvent<number>(OPEN_DEMAND_COUNT_EVENT, { detail: openCount }));
  }, [requests, onDemandCountChange]);

  // ── Drawer state ──────────────────────────────────────────────────────────
  const [drawerOpen, setDrawerOpen]                 = useState(false);
  const [activeRequest, setActiveRequest]           = useState<ResourceRequest | null>(null);
  const [activeScenariosMap, setActiveScenariosMap] = useState<Record<string, PlanScenario[]>>({});
  const activeScenariosMapRef = useRef(activeScenariosMap);
  activeScenariosMapRef.current = activeScenariosMap;
  // The active request's role budget, so the Accept re-check judges the plan
  // against the same monthly budget the drawer validates edits against.
  const { roleBudgets: activeRoleBudgets } = useProjectBudget(activeRequest?.projectId);
  const [isGenerating, setIsGenerating]             = useState(false);
  // Bookings already created for the active request — the read-only record of
  // an accepted plan (the accepted scenario itself is never persisted).
  const [acceptedBookings, setAcceptedBookings]     = useState<AcceptedRequestBooking[]>([]);
  const [acceptedBookingsLoading, setAcceptedBookingsLoading] = useState(false);
  // 'bookings' opens straight on the read-only record of what is already booked,
  // skipping scenario generation for the hours that remain.
  const [drawerView, setDrawerView] = useState<'plan' | 'bookings'>('plan');

  // ── Stable refs used by the auto-open effect (the handler is assigned below) ──
  const requestsRef = useRef(requests);
  requestsRef.current = requests;
  const handleViewPlanRef = useRef<((request: ResourceRequest) => void) | null>(null);

  // Read through refs so the fetchers below keep a stable identity: `resources`
  // and the company can change identity on any parent render, and a fetcher
  // that changed with them re-fired every handler and effect that used it.
  const resourcesRef = useRef(resources);
  resourcesRef.current = resources;
  const companyIdRef = useRef(currentCompany?.id);
  companyIdRef.current = currentCompany?.id;
  const isDemoModeRef = useRef(isDemoMode);
  isDemoModeRef.current = isDemoMode;

  // Guards for the accepted-bookings read: the open request is aborted when a
  // newer one supersedes it, only the latest read writes state, and a repeat of
  // the read already in flight is dropped.
  const acceptedBookingsAbortRef = useRef<AbortController | null>(null);
  const acceptedBookingsFetchIdRef = useRef(0);
  const inFlightAcceptedKeyRef = useRef<string | null>(null);
  const resolvedResourcesRef = useRef(new Map<string, ResolvedBookingResource>());

  // ── Table state ───────────────────────────────────────────────────────────
  const [expandedRow, setExpandedRow]       = useState<string | null>(null);
  const [searchQuery, setSearchQuery]       = useState('');
  const [statusFilter, setStatusFilter]     = useState<StatusFilter>(RR_DEFAULT_STATUS_FILTER);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [priorityFilter, setPriorityFilter] = useState<PriorityFilter>('all');
  const [sortField, setSortField]           = useState<SortField>('priority');
  const [sortDir, setSortDir]               = useState<SortDir>('none');
  const [page, setPage]                     = useState(1);
  const [pageSize, setPageSize]             = useState<number>(DEFAULT_PAGE_SIZE);

  // ── Column configuration state ─────────────────────────────────────────────
  const [rrVisibleColumns, setRrVisibleColumns] = useState<Set<RRColumnKey>>(new Set(RR_DEFAULT_VISIBLE));
  const [rrColumnOrder, setRrColumnOrder]       = useState<RRColumnKey[]>(RR_DEFAULT_ORDER);
  const [rrConfigOpen, setRrConfigOpen]         = useState(false);

  const rrVisibleCount = RR_ALL_COLUMNS.filter(c => rrVisibleColumns.has(c.key)).length;

  const rrToggleColumn = (key: RRColumnKey) => {
    const def = RR_ALL_COLUMNS.find(c => c.key === key);
    if (def?.locked) return;
    setRrVisibleColumns(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const rrResetColumns = () => {
    setRrVisibleColumns(new Set(RR_DEFAULT_VISIBLE));
    setRrColumnOrder(RR_DEFAULT_ORDER);
  };

  const rrHandleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setRrColumnOrder(prev => {
        const oldIdx = prev.indexOf(active.id as RRColumnKey);
        const newIdx = prev.indexOf(over.id as RRColumnKey);
        return arrayMove(prev, oldIdx, newIdx);
      });
    }
  };

  const rrDndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // ── Hours booked by hand for a request's project and role ─────────────────
  // A booking made straight in the planner never carries a demandId, so the
  // server's allocated hours miss it. Those bookings are rolled up per request so
  // the hours left to plan match what is really on the calendar: once for the
  // whole list below, and again from the single read the drawer makes of the
  // project of the demand being opened.
  const [manualHoursByRequestId, setManualHoursByRequestId] =
    useState<Map<string, ManualBookedHours>>(new Map());

  const roleByResourceId = useMemo(
    () => new Map(resources.map(r => [String(r.id), r.role])),
    [resources],
  );

  const roleByResourceIdRef = useRef(roleByResourceId);
  roleByResourceIdRef.current = roleByResourceId;

  // Roles by value, so a new `resources` array with the same content does not
  // re-run the roll-up below.
  const resourceRolesKey = useMemo(
    () => resources.map(r => `${r.id}:${r.role ?? ''}`).join(','),
    [resources],
  );

  const projectIdsKey = useMemo(
    () => [...new Set(requests.map(r => String(r.projectId)).filter(Boolean))].sort().join(','),
    [requests],
  );
  const manualHoursDataKey = [
    isDemoMode ? 'demo' : 'live',
    currentCompany?.id ?? '',
    projectIdsKey,
  ].join('|');
  const [manualHoursSettledKey, setManualHoursSettledKey] = useState<string | null>(null);
  const manualHoursPending = manualHoursSettledKey !== manualHoursDataKey;

  // The rows need the hand-made hours before any demand is opened, which takes
  // the company's bookings and its projects — two reads for the whole list,
  // where reading each demand's project separately took one per project and ran
  // the API into its rate limit. `Projects.externalId` is what a demand names as
  // its project, so it maps the bookings back onto the requests.
  useEffect(() => {
    const companyId = currentCompany?.id;
    // A settled key is retained; a changed data key gates until its pass settles.
    const settleManualHours = () => setManualHoursSettledKey(manualHoursDataKey);
    if (isDemoMode || !companyId || !projectIdsKey) {
      setManualHoursByRequestId(new Map());
      settleManualHours();
      return;
    }
    const controller = new AbortController();
    Promise.all([
      bookingsApi.getAll(false, companyId, controller.signal),
      projectsApi.getAll(companyId),
    ]).then(([bookings, projects]) => {
      if (controller.signal.aborted) return;
      const wantedExternalIds = new Set(projectIdsKey.split(','));
      const externalByProjectId = new Map(
        projects
          .filter(p => p.externalId && wantedExternalIds.has(String(p.externalId)))
          .map(p => [String(p.id), String(p.externalId)]),
      );
      const bookingsByProject = new Map<string, ApiBooking[]>();
      for (const booking of bookings) {
        const externalId = externalByProjectId.get(String(booking.projectId));
        if (!externalId) continue;
        const forProject = bookingsByProject.get(externalId);
        if (forProject) forProject.push(booking);
        else bookingsByProject.set(externalId, [booking]);
      }
      const next = new Map<string, ManualBookedHours>();
      for (const request of requestsRef.current) {
        const projectBookings = bookingsByProject.get(String(request.projectId));
        if (!projectBookings?.length) continue;
        const manual = manualBookedHoursForRequest(projectBookings, request, roleByResourceIdRef.current);
        if (manual.total > 0) next.set(request.id, manual);
      }
      setManualHoursByRequestId(next);
      settleManualHours();
    }).catch(() => {
      // Non-fatal: the rows fall back to the hours the demands API reported
      if (!controller.signal.aborted) settleManualHours();
    });
    return () => controller.abort();
  }, [isDemoMode, currentCompany?.id, projectIdsKey, resourceRolesKey, manualHoursDataKey]);

  // ── Live (non-deleted) projects ───────────────────────────────────────────
  // The demands API names its project by `Projects.externalId`. The projects
  // endpoint excludes soft-deleted projects by default, so any demand whose
  // project is missing from this set belongs to a removed project. It stays
  // undefined until the projects load, so nothing is flagged prematurely.
  const [liveProjectExternalIds, setLiveProjectExternalIds] = useState<ReadonlySet<string> | undefined>(undefined);

  useEffect(() => {
    const companyId = currentCompany?.id;
    if (isDemoMode || !companyId) {
      setLiveProjectExternalIds(undefined);
      return;
    }
    let cancelled = false;
    projectsApi.getAll(companyId).then(projects => {
      if (cancelled) return;
      setLiveProjectExternalIds(new Set(
        projects.map(p => String(p.externalId ?? '')).filter(Boolean),
      ));
    }).catch(() => {
      // Non-fatal: without the live set no request is marked as removed
    });
    return () => { cancelled = true; };
  }, [isDemoMode, currentCompany?.id]);

  // ── Compute priority dynamically from thresholds ─────────────────────────
  // Priority on the stored request is now only a fallback for accepted/declined
  // requests where "days until start" is no longer a meaningful signal.
  const resolvedRequests = useMemo(() =>
    requests.map(request => {
      const r = applyManualBookedHours(request, manualHoursByRequestId.get(request.id) ?? NO_MANUAL_HOURS);
      if (isProjectRemoved(r.projectId, liveProjectExternalIds)) {
        return { ...r, status: 'project-removed' as const };
      }
      return {
        ...r,
        priority: (r.status === 'accepted' || RR_TERMINAL_STATUSES.has(r.status))
          ? r.priority                                        // keep original for closed requests
          : computeRequestPriority(r.requestedStartDate, thresholds),
      };
    }),
  [requests, thresholds, manualHoursByRequestId, liveProjectExternalIds]);

  // ── Filtered + sorted ─────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    const base = resolvedRequests.filter(r => {
      // Accepted requests are closed, so they only appear when explicitly asked
      // for via the status filter.
      if (!statusFilter.includes('accepted') && r.status === 'accepted') return false;
      if (q) {
        const hit =
          r.projectName.toLowerCase().includes(q) ||
          r.role.toLowerCase().includes(q) ||
          r.requestedByName.toLowerCase().includes(q) ||
          r.skills.some(s => s.toLowerCase().includes(q)) ||
          (r.projectCode?.toLowerCase().includes(q) ?? false);
        if (!hit) return false;
      }
      if (statusFilter.length > 0 && !statusFilter.includes(r.status))  return false;
      if (priorityFilter !== 'all' && r.priority !== priorityFilter) return false;
      return true;
    });
    return sortRequests(base, sortField, sortDir);
  }, [resolvedRequests, searchQuery, statusFilter, priorityFilter, sortField, sortDir]);

  // Total of the base set the table is allowed to show, i.e. accepted requests
  // only count when the status filter explicitly asks for them.
  const totalVisible = useMemo(() =>
    statusFilter.includes('accepted')
      ? resolvedRequests.length
      : resolvedRequests.filter(r => r.status !== 'accepted').length,
  [resolvedRequests, statusFilter]);

  const paginated = useMemo(
    () => paginate(filtered, page, pageSize),
    [filtered, page, pageSize],
  );

  // Filters and sorting can shrink the result set below the current page
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    if (page > totalPages) setPage(totalPages);
  }, [filtered.length, page, pageSize]);

  const isFiltered = !!(searchQuery || !isDefaultStatusFilter(statusFilter) || priorityFilter !== 'all');

  const toggleStatusFilter = useCallback((status: ResourceRequest['status']) => {
    setPage(1);
    setStatusFilter(prev => prev.includes(status)
      ? prev.filter(s => s !== status)
      : [...prev, status]);
  }, []);

  // ── Sort handler ─────────────────────────────────────────────────────────
  // ── Sort handler — 3-state: asc → desc → none ────────────────────────────
  const handleSort = useCallback((field: SortField) => {
    setPage(1);
    setSortField(prev => {
      if (prev === field) {
        setSortDir(d => {
          if (d === 'asc')  return 'desc';
          if (d === 'desc') return 'none';
          return 'asc';
        });
        return field;
      }
      setSortDir('asc');
      return field;
    });
  }, []);

  /**
   * The bookings inside the plan window, so the planner measures capacity against
   * the same bookings the drawer then validates the plan against.
   *
   * In live mode the tab is handed an empty booking list, which used to let the
   * planner propose days a resource had no time left on — the drawer, which does
   * fetch this window, then greeted the plan with "fully booked on <day>".
   */
  const planWindowBookings = useCallback(async (request: ResourceRequest): Promise<Booking[]> => {
    const companyId = companyIdRef.current;
    const bounds = computePlanDateBounds(
      request.requestedStartDate,
      monthsForRequest(request),
      request.requestedEndDate,
    );
    if (isDemoModeRef.current || !companyId || !bounds) return existingBookings;

    try {
      const apiBookings = await bookingsApi.getByDateRange(
        new Date(`${bounds.minDate}T00:00:00Z`).toISOString(),
        new Date(`${bounds.maxDate}T23:59:59Z`).toISOString(),
        false,
        companyId,
      );
      const fetched = apiBookings.map(mapApiBookingToBooking);
      const byId = new Map(fetched.map(booking => [String(booking.id), booking]));
      for (const booking of existingBookings) byId.set(String(booking.id), booking);
      return [...byId.values()];
    } catch {
      // Non-fatal: the plan is still measured against the backend's per-day
      // booked hours, as it was before.
      return existingBookings;
    }
  }, [existingBookings]);

  /**
   * The absences inside the plan window, so a day a resource is on leave for is
   * not proposed — in full for a whole day, or in the half a half-day absence
   * takes. The same source the drawer validates the plan against.
   */
  const planWindowAbsences = useCallback(async (request: ResourceRequest): Promise<Absence[]> => {
    if (isDemoModeRef.current) return mockAbsences;
    const companyId = companyIdRef.current;
    const bounds = computePlanDateBounds(
      request.requestedStartDate,
      monthsForRequest(request),
      request.requestedEndDate,
    );
    if (!companyId || !bounds) return [];

    try {
      return await absencesApi.getByDateRange(
        new Date(`${bounds.minDate}T00:00:00Z`).toISOString(),
        new Date(`${bounds.maxDate}T23:59:59Z`).toISOString(),
        companyId,
      );
    } catch {
      // Non-fatal: the plan is sized as it was before, and the drawer still
      // reports any day an absence leaves it over capacity.
      return [];
    }
  }, []);

  // ── Plan handlers ─────────────────────────────────────────────────────────
  const handleGeneratePlan = useCallback(async (request: ResourceRequest) => {
    setActiveRequest(request);
    setDrawerOpen(true);
    setIsGenerating(true);
    try {
      // generatePlanScenarios is now async (calls backend API for fresh availability)
      const [windowBookings, windowAbsences] = await Promise.all([
        planWindowBookings(request),
        planWindowAbsences(request),
      ]);
      const scenarios = await generatePlanScenarios({
        request,
        resources,
        existingBookings: windowBookings,
        absences: windowAbsences,
        bankHolidays: mockBankHolidays,
        globalWorkingProfile,
        businessHoursStart,
        businessHoursEnd,
      });
      setActiveScenariosMap(prev => ({ ...prev, [request.id]: scenarios }));
      // Only an untouched request becomes 'plan-ready'. A partially allocated one
      // keeps its status so the row still offers "View" alongside "Plan remaining".
      setRequests(prev => prev.map(r =>
        r.id === request.id && r.status === 'pending' ? { ...r, status: 'plan-ready' as const } : r,
      ));
    } catch (err) {
      const description = err instanceof ApiError
        ? (err.details || err.getUserMessage())
        : 'An error occurred while analysing resources.';
      toast.error('Failed to generate plan', { description });
    } finally {
      setIsGenerating(false);
    }
  }, [
    resources, planWindowBookings, planWindowAbsences, globalWorkingProfile,
    businessHoursStart, businessHoursEnd,
  ]);

  // ── Auto-open drawer when returning from calendar preview ─────────────────
  useEffect(() => {
    if (!initialOpenRequestId) return;
    const target = requestsRef.current.find(r => r.id === initialOpenRequestId);
    if (!target) return;
    // Same path as opening the drawer by hand, so the project is read once and
    // the plan is generated against the hours that are really left.
    handleViewPlanRef.current?.(target);
    onInitialOpenRequestIdConsumed?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOpenRequestId, requests]);

  /**
   * Read the request's project once — that project only, for the current company
   * — and take both things the drawer needs from the one response: the bookings
   * a previously accepted plan created (stamped with the demand they came from),
   * and the hours booked by hand for the request's role.
   *
   * Returns the hand-made hours, or null when nothing was read.
   */
  const loadProjectBookings = useCallback(async (
    request: ResourceRequest,
    options?: { force?: boolean },
  ): Promise<ManualBookedHours | null> => {
    const companyId = companyIdRef.current;
    setAcceptedBookings([]);
    if (isDemoModeRef.current || !request.projectId) return null;

    // The drawer can ask for the same bookings from several places while one
    // read is still open (open, auto-open, re-render), so an identical request
    // in flight is left to finish instead of being issued again. The accept
    // flow forces a re-read because the bookings have just changed.
    const key = `${request.projectId}|${companyId ?? ''}|${request.id}`;
    if (!options?.force && inFlightAcceptedKeyRef.current === key) return null;

    acceptedBookingsAbortRef.current?.abort();
    const controller = new AbortController();
    acceptedBookingsAbortRef.current = controller;
    const fetchId = ++acceptedBookingsFetchIdRef.current;
    inFlightAcceptedKeyRef.current = key;

    setAcceptedBookingsLoading(true);
    try {
      const demandId = Number(request.id);
      const projectBookings = await bookingsApi.getByExternalProjectId(request.projectId, companyId, controller.signal);
      if (fetchId !== acceptedBookingsFetchIdRef.current) return null;
      const demandBookings = projectBookings.filter(b => Number(b.demandId) === demandId);

      const manual = manualBookedHoursForRequest(projectBookings, request, roleByResourceIdRef.current);
      setManualHoursByRequestId(prev => {
        const existing = prev.get(request.id);
        if (manual.total <= 0) {
          if (!existing) return prev;
          const next = new Map(prev);
          next.delete(request.id);
          return next;
        }
        if (existing?.total === manual.total) return prev;
        return new Map(prev).set(request.id, manual);
      });

      const resourceById = new Map<string, ResolvedBookingResource>(
        resourcesRef.current.map(r => [String(r.id), { name: r.name, role: r.role, photoUrl: r.photoUrl }]),
      );
      // Bookings can reference resources outside the list this tab was given
      // (another company, or an inactive resource), so look those up by id
      // rather than falling back to "Resource <id>". Ids resolved on an earlier
      // open are remembered, so re-opening the drawer looks up nothing.
      const unknownIds = [...new Set(
        demandBookings
          .map(b => String(b.resourceId))
          .filter(id => id && id !== 'null' && !resourceById.has(id)),
      )];
      for (const id of unknownIds) {
        const cached = resolvedResourcesRef.current.get(id);
        if (cached) resourceById.set(id, cached);
      }
      const idsToLookUp = unknownIds.filter(id => !resourceById.has(id));
      // Looked up a few at a time so a demand spread over many outside
      // resources cannot fire a burst of parallel /resources/{id} requests.
      for (let i = 0; i < idsToLookUp.length; i += RESOURCE_LOOKUP_BATCH_SIZE) {
        if (fetchId !== acceptedBookingsFetchIdRef.current) return null;
        await Promise.all(idsToLookUp.slice(i, i + RESOURCE_LOOKUP_BATCH_SIZE).map(async id => {
          try {
            const resource = await resourcesApi.getById(id);
            if (resource?.name) {
              const resolved = { name: resource.name, role: resource.role, photoUrl: resource.photoUrl };
              resolvedResourcesRef.current.set(id, resolved);
              resourceById.set(id, resolved);
            }
          } catch {
            // Leave it unresolved — the row falls back to the id below
          }
        }));
      }

      if (fetchId !== acceptedBookingsFetchIdRef.current) return null;
      setAcceptedBookings(
        demandBookings
          .map(b => {
            const resource = resourceById.get(String(b.resourceId));
            return {
              id:               String(b.id),
              resourceId:       String(b.resourceId),
              resourceName:     resource?.name ?? `Resource ${b.resourceId}`,
              resourceRole:     resource?.role,
              resourcePhotoUrl: resource?.photoUrl,
              monthKey:         String(b.startTime).slice(0, 7),
              startTime:        b.startTime,
              endTime:          b.endTime,
              hours:            Number(b.durationHours ?? 0),
              status:           b.status,
            };
          })
          .sort((a, b) => a.startTime.localeCompare(b.startTime)),
      );
      return manual;
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return null;
      if (fetchId !== acceptedBookingsFetchIdRef.current) return null;
      // Non-fatal: the drawer falls back to the demand's per-month allocated hours
      setAcceptedBookings([]);
      return null;
    } finally {
      // A superseded read must not clear the flag while a newer one is open.
      if (fetchId === acceptedBookingsFetchIdRef.current) {
        inFlightAcceptedKeyRef.current = null;
        setAcceptedBookingsLoading(false);
      }
    }
  }, []);

  useEffect(() => () => {
    acceptedBookingsAbortRef.current?.abort();
  }, []);

  /**
   * The drawer's copy of the request with the hand-made hours just read folded
   * in — the table rows pick the same hours up through `manualHoursByRequestId`.
   * A request the user has already navigated away from is left alone.
   */
  const foldManualHoursIntoActive = useCallback((
    raw: ResourceRequest,
    manual: ManualBookedHours,
  ): ResourceRequest => {
    const adjusted = applyManualBookedHours(raw, manual);
    setActiveRequest(prev => prev && prev.id !== raw.id
      ? prev
      : { ...adjusted, priority: prev?.priority ?? adjusted.priority });
    return adjusted;
  }, []);

  /** The request as the API last gave it, i.e. before any manual hours were folded in. */
  const rawRequest = useCallback((request: ResourceRequest) =>
    requestsRef.current.find(r => r.id === request.id) ?? request, []);

  /** Read-only view of the bookings already created for a partially allocated request. */
  const handleViewBookings = useCallback(async (request: ResourceRequest) => {
    setActiveRequest(request);
    setDrawerView('bookings');
    setDrawerOpen(true);
    setIsGenerating(false);
    const manual = await loadProjectBookings(request);
    if (manual) foldManualHoursIntoActive(rawRequest(request), manual);
  }, [loadProjectBookings, foldManualHoursIntoActive, rawRequest]);

  const handleViewPlan = useCallback(async (request: ResourceRequest) => {
    setActiveRequest(request);
    setDrawerView('plan');
    setDrawerOpen(true);
    setIsGenerating(false);
    // The project is read before planning, not only to show what is already
    // booked but because the hand-made hours it reveals are hours the plan must
    // not offer again.
    const manual = await loadProjectBookings(request);
    const planFor = manual ? foldManualHoursIntoActive(rawRequest(request), manual) : request;
    // A fully booked request has no outstanding hours to plan — the drawer
    // shows the bookings the accepted plan created instead.
    if (planFor.totalHoursRequested <= 0) return;
    if (!activeScenariosMapRef.current[request.id]) {
      handleGeneratePlan(planFor);
    }
  }, [loadProjectBookings, foldManualHoursIntoActive, rawRequest, handleGeneratePlan]);

  handleViewPlanRef.current = handleViewPlan;

  /**
   * Drops a request's generated scenarios so the next open re-plans against the
   * hours that are still outstanding. A cached plan was built for the hours the
   * request had *before* its bookings were created, so re-using it would suggest
   * work that is already booked.
   */
  const invalidateScenarios = useCallback((requestId: string) => {
    setActiveScenariosMap(prev => {
      if (!(requestId in prev)) return prev;
      const next = { ...prev };
      delete next[requestId];
      return next;
    });
  }, []);

  const handleDecline = useCallback(async (requestId: string) => {
    if (isDemoMode) {
      setRequests(prev => prev.map(r => r.id === requestId ? { ...r, status: 'declined' as const } : r));
      toast.info('Request declined');
      return;
    }
    try {
      await demandsApi.updateStatus(Number(requestId), 'cancelled');
      await fetchDemands();
      toast.info('Request declined');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to decline demand';
      toast.error('Decline failed', { description: message });
    }
  }, [fetchDemands, isDemoMode]);

  /**
   * Creates the bookings of an accepted plan. `slots` already carry the drawer's
   * adjustments, and each slot's daily breakdown is sent verbatim so every day of
   * the reviewed plan becomes its own booking.
   */
  const handleAccept = useCallback(async (
    scenarioId: string,
    slots: ProposedBookingSlot[],
    editCount: number,
    bookingTypeId?: number,
    unavailableDaysByResourceId?: Record<string, UnavailableDay[]>,
  ) => {
    if (!activeRequest) return;
    const scenario = activeScenariosMap[activeRequest.id]?.find(s => s.id === scenarioId);
    if (!scenario) return;

    const editNote      = editCount > 0 ? ` (${editCount} booking${editCount !== 1 ? 's' : ''} adjusted)` : '';
    const resourceCount = new Set(slots.map(s => s.resourceId)).size;

    // The drawer disables Accept while an edit is invalid; re-check here so a
    // plan that breaks a resource's working profile can never be allocated. The
    // slots already carry their adjustments, so they are judged as they are.
    // The scenario's per-day utilisation goes with them: in live mode it is the
    // only account of the resource's existing bookings the client has.
    const activeRoleBudget = findRoleBudget(
      activeRoleBudgets, activeRequest.role, activeRequest.roleCode,
    );
    const budgetedHoursByMonth = parseMonthlyHours(activeRoleBudget?.monthlyHours);

    const slotIssues = validatePlanSlots({
      slots,
      existingBookings,
      resources,
      globalWorkingProfile,
      budgetedHoursByMonth: Object.keys(budgetedHoursByMonth).length > 0
        ? budgetedHoursByMonth
        : undefined,
      bookedHoursByMonth: activeRequest.monthlyAllocatedHours,
      reportedBookedDaysByResourceId: scenario.resourceDailyUtilisation,
      unavailableDaysByResourceId,
    });
    const invalidSlots = blockingErrorsOnly(slotIssues);
    if (invalidSlots.size > 0) {
      toast.error('Plan cannot be accepted', {
        description: Array.from(invalidSlots.values())[0].message,
      });
      return;
    }

    // Budget overage is informational: the plan is allocated anyway, with the
    // months it exceeds spelled out so the user knows what they accepted.
    const budgetWarnings = warningsOnly(slotIssues);
    if (budgetWarnings.size > 0) {
      toast.warning('Plan exceeds the budgeted hours', {
        description: Array.from(budgetWarnings.values())[0].message,
      });
    }


    // The Booking Type is chosen on the confirmation step and is honoured even when
    // the user only has view access to it, so the only requirement here is that the
    // company has at least one active Booking Type to stamp on the bookings.
    if (!isDemoMode && (settings.bookingTypes ?? []).every(bt => !bt.active)) {
      toast.error('No booking type available', {
        description: 'No active booking type is configured, so bookings cannot be created from this plan. Please contact your administrator.',
      });
      return;
    }

    if (isDemoMode) {
      const demandPlanGroupId = crypto.randomUUID();
      const demoDemandId = Number.parseInt(activeRequest.id.replace(/\D/g, ''), 10) || 0;
      const selectedBookingTypeId = String(
        bookingTypeId
        ?? settings.defaultBookingTypeId
        ?? settings.bookingTypes?.find(bt => bt.active)?.id
        ?? 'bt-1',
      );
      const demoBookings: Omit<Booking, 'id'>[] = slots.flatMap(slot =>
        (slot.dailyBreakdown ?? [])
          .filter(day => day.hours > 0 && day.startTime && day.endTime)
          .map(day => ({
            resourceId: String(slot.resourceId),
            resourceIds: [String(slot.resourceId)],
            projectId: String(activeRequest.projectId),
            projectName: activeRequest.projectName,
            bookingTypeId: selectedBookingTypeId,
            description: activeRequest.notes,
            startTime: day.startTime,
            endTime: day.endTime,
            timezone: 'UTC',
            color: getProjectColor(activeRequest.projectName),
            durationType: 'hourly' as const,
            durationHours: day.hours,
            durationDays: 0,
            billingClassification: 'billable' as const,
            workType: 'remote' as const,
            status: 'confirmed' as const,
            priority: activeRequest.priority === 'critical' ? 'high' : activeRequest.priority,
            requiredRole: activeRequest.role,
            requiredSkills: activeRequest.skills,
            source: DEMAND_SOURCE,
            demandId: demoDemandId,
            demandPlanGroupId,
          })),
      );
      await createBatchBookings(demoBookings, 'demo');
      setRequests(prev => prev.map(r =>
        r.id === activeRequest.id ? { ...r, status: 'accepted' as const, acceptedScenarioId: scenarioId } : r,
      ));
      setActiveRequest(prev => prev && prev.id === activeRequest.id
        ? { ...prev, status: 'accepted' as const, acceptedScenarioId: scenarioId }
        : prev);
      invalidateScenarios(activeRequest.id);
      const demoBookingCount = demoBookings.length;
      toast.success('Plan accepted — bookings created', {
        description: `${demoBookingCount} booking${demoBookingCount !== 1 ? 's' : ''} across ${resourceCount} resource${resourceCount !== 1 ? 's' : ''}${editNote}.`,
      });
      return;
    }

    const demandId = Number(activeRequest.id);
    const demand = rawDemands.find(d => d.id === demandId);
    const demandLines = demand?.demandLines ?? [];

    try {
      // Build one batch item per slot using the same per-slot logic as before, then
      // send a single request so accepting a multi-slot plan issues one HTTP call.
      const items: AllocateDailyBatchItem[] = [];

      for (const slot of slots) {
        // Match slot to a DemandLine by month key (YYYY-MM format)
        const matchedLine = findDemandLineByMonth(demandLines, slot.monthKey);
        const targetLine = matchedLine ?? demandLines.find(l => l.status !== 'filled');
        if (!targetLine) continue;

        // Send the plan's own per-day windows so the backend books exactly the days
        // shown, one booking each. Without them it would re-spread the total hours
        // over the range and collapse a multi-day plan into fewer bookings.
        const days = (slot.dailyBreakdown ?? [])
          .filter(d => d.hours > 0 && d.startTime && d.endTime)
          .map(d => ({ startTime: d.startTime, endTime: d.endTime }));

        items.push({
          lineId: targetLine.id,
          resourceId: Number(slot.resourceId),
          totalHours: slot.hours,
          startDate: slot.startDate,
          endDate: slot.endDate,
          ...(days.length > 0 ? { days } : {}),
          ...(bookingTypeId ? { bookingTypeId } : {}),
        });
      }

      const result = await demandsApi.allocateLinesDailyBatch(demandId, { items });
      const totalBookingsCreated = result?.bookings?.length ?? 0;

      // Refresh from API — status, allocated hours and the outstanding hours are
      // all recalculated server-side, and the plan that has just been booked is
      // dropped so viewing the request again plans only what is left.
      const refreshed = await fetchDemands();
      invalidateScenarios(String(demandId));
      const latest = refreshed.find(r => r.id === String(demandId));
      if (latest) {
        setActiveRequest(latest);
        const manual = await loadProjectBookings(latest, { force: true });
        if (manual) foldManualHoursIntoActive(latest, manual);
      }

      toast.success('Plan accepted — bookings created', {
        description: `${totalBookingsCreated} booking${totalBookingsCreated !== 1 ? 's' : ''} across ${resourceCount} resource${resourceCount !== 1 ? 's' : ''}${editNote}.`,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to allocate demand';
      toast.error('Allocation failed', { description: message });
    }
  }, [activeRequest, activeScenariosMap, rawDemands, fetchDemands, loadProjectBookings,
    foldManualHoursIntoActive, invalidateScenarios, isDemoMode, settings.bookingTypes,
    settings.defaultBookingTypeId,
    existingBookings, resources, globalWorkingProfile, activeRoleBudgets, createBatchBookings]);

  const handleClearFilters = useCallback(() => {
    setPage(1);
    setSearchQuery('');
    setStatusFilter(RR_DEFAULT_STATUS_FILTER);
    setPriorityFilter('all');
  }, []);

  const toggleRow = useCallback((id: string) => {
    setExpandedRow(prev => prev === id ? null : id);
  }, []);

  // ── Export handler ────────────────────────────────────────────────────────
  const handleExportToExcel = useCallback(() => {
    const rows = filtered.map(r => ({
      'Priority':       PRIORITY_CONFIG[r.priority].label,
      'Project':        r.projectName,
      'Project Code':   r.projectCode ?? '',
      'Role':           r.role,
      'Hours':          r.totalHoursRequested,
      'Start Date':     fmtDate(r.requestedStartDate),
      'Status':         STATUS_CONFIG[r.status].label,
      'Requested By':   r.requestedByName,
      'Received':       fmtDate(r.receivedAt),
      'Skills':         r.skills.join(', '),
      'Location':       r.location ?? '',
      'Notes':          r.notes ?? '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook  = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Resource Requests');

    // Auto-size columns
    const colWidths = Object.keys(rows[0] ?? {}).map(key => ({
      wch: Math.max(key.length, ...rows.map(r => String(r[key as keyof typeof r]).length)) + 2,
    }));
    worksheet['!cols'] = colWidths;

    XLSX.writeFile(workbook, `resource-requests-${new Date().toISOString().slice(0, 10)}.xlsx`);

    toast.success('Export complete', {
      description: `${filtered.length} request${filtered.length !== 1 ? 's' : ''} exported to Excel.`,
    });
  }, [filtered]);

  const activeScenarios = activeRequest ? (activeScenariosMap[activeRequest.id] ?? []) : [];
  const acceptedCount   = resolvedRequests.filter(r => r.status === 'accepted').length;
  const partiallyAllocatedCount = resolvedRequests.filter(r => r.status === 'partially-allocated').length;
  const pendingCount    = resolvedRequests.filter(r => r.status === 'pending').length;
  const planReadyCount  = resolvedRequests.filter(r => r.status === 'plan-ready').length;
  const declinedCount   = resolvedRequests.filter(r => r.status === 'declined').length;
  const projectRemovedCount = resolvedRequests.filter(r => r.status === 'project-removed').length;

  const statusCounts: Record<ResourceRequest['status'], number> = {
    'pending':             pendingCount,
    'plan-ready':          planReadyCount,
    'partially-allocated': partiallyAllocatedCount,
    'declined':            declinedCount,
    'project-removed':     projectRemovedCount,
    'accepted':            acceptedCount,
  };

  // The "All Statuses" row is ticked only while every individual status is ticked.
  const allStatusesSelected = RR_STATUS_FILTER_OPTIONS.every(s => statusFilter.includes(s));

  const statusFilterLabel =
    statusFilter.length === 0 || allStatusesSelected ? 'All Statuses'
    : statusFilter.length === 1 ? STATUS_CONFIG[statusFilter[0]].label
    : `${statusFilter.length} statuses`;

  // ── Loading state ──────────────────────────────────────────────────────────
  if (loadingState === 'loading') {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Loading resource demands...</span>
      </div>
    );
  }

  // ── Error state ────────────────────────────────────────────────────────────
  if (loadingState === 'error') {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-sm text-destructive mb-3">Failed to load resource demands</p>
        <Button variant="outline" size="sm" onClick={() => { void fetchDemands(); }}>
          Retry
        </Button>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">

      {/* ── Filter bar ── */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            placeholder="Search project, role, skill…"
            value={searchQuery}
            onChange={e => { setPage(1); setSearchQuery(e.target.value); }}
            className="pl-9 h-9 text-sm bg-card"
          />
        </div>

        <Popover open={statusMenuOpen} onOpenChange={setStatusMenuOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={statusMenuOpen}
              aria-label="Filter by status"
              className="h-9 w-[180px] justify-between bg-card px-3 text-sm font-normal"
            >
              <span className="truncate">{statusFilterLabel}</span>
              <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[220px] p-1">
            <button
              type="button"
              onClick={() => { setPage(1); setStatusFilter(allStatusesSelected ? [] : [...RR_STATUS_FILTER_OPTIONS]); }}
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
            >
              <Checkbox checked={allStatusesSelected} className="pointer-events-none" />
              All Statuses
            </button>
            {RR_STATUS_FILTER_OPTIONS.map(status => (
              <button
                key={status}
                type="button"
                onClick={() => toggleStatusFilter(status)}
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
              >
                <Checkbox checked={statusFilter.includes(status)} className="pointer-events-none" />
                <span className="flex items-center gap-2">
                  {STATUS_CONFIG[status].label}
                  {statusCounts[status] > 0 && (
                    <span className="text-xs text-muted-foreground tabular-nums">({statusCounts[status]})</span>
                  )}
                </span>
              </button>
            ))}
          </PopoverContent>
        </Popover>

        <Select value={priorityFilter} onValueChange={v => { setPage(1); setPriorityFilter(v as PriorityFilter); }}>
          <SelectTrigger className="h-9 w-[140px] text-sm bg-card">
            <SelectValue placeholder="All Priorities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            {(['critical', 'high', 'medium', 'low'] as RequestPriority[]).map(p => (
              <SelectItem key={p} value={p}>
                <span className="flex items-center gap-2">
                  <span
                    className={cn('h-2 w-2 rounded-full shrink-0', PRIORITY_CONFIG[p].dotAccent)}
                    style={{ backgroundColor: 'var(--accent-border)' }}
                  />
                  {PRIORITY_CONFIG[p].label}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {isFiltered && (
          <Button variant="ghost" size="sm" className="h-9 text-xs text-muted-foreground gap-1.5" onClick={handleClearFilters}>
            <Filter className="h-3.5 w-3.5" />
            Clear
          </Button>
        )}

        {/* ── Divider + icon action buttons ── */}
        <div className="h-6 w-px bg-border shrink-0" />

        <TooltipProvider delayDuration={300}>
          {/* Refresh requests */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9 shrink-0"
                onClick={() => { void handleRefresh(); }}
                disabled={isRefreshing}
                aria-label="Refresh resource requests"
              >
                <RefreshCw className={cn('h-4 w-4', isRefreshing && 'animate-spin')} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Refresh resource requests</TooltipContent>
          </Tooltip>

          {/* Export to Excel */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9 shrink-0"
                onClick={handleExportToExcel}
                disabled={filtered.length === 0}
                aria-label="Export to Excel"
              >
                <Download className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Export to Excel</TooltipContent>
          </Tooltip>

          {/* Configure Columns */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9 shrink-0 relative"
                onClick={() => setRrConfigOpen(true)}
                aria-label="Configure columns"
              >
                <Settings2 className="h-4 w-4" />
                {rrVisibleCount < RR_ALL_COLUMNS.length && (
                  <span className="absolute -top-1 -right-1 h-3.5 w-3.5 rounded-full bg-primary text-[9px] font-bold text-primary-foreground flex items-center justify-center leading-none">
                    {rrVisibleCount}
                  </span>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>Configure columns ({rrVisibleCount} of {RR_ALL_COLUMNS.length} visible)</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <span className="ml-auto text-xs text-muted-foreground tabular-nums">
          {filtered.length} of {totalVisible}
        </span>
      </div>

      {/* ── Table ── */}
      <div className={TABLE_CONTAINER_CLASS}>
        <Table>
          <TableHeader>
            <TableRow className={TABLE_HEADER_ROW_CLASS}>
              {/* Expand toggle — no label */}
              <TableHead className="w-8 px-3" />

              {rrColumnOrder.filter(k => rrVisibleColumns.has(k)).map(key => {
                const colDef = RR_ALL_COLUMNS.find(c => c.key === key)!;
                if (!colDef.sortField) return null;
                const colClassMap: Record<RRColumnKey, string> = {
                  priority:  'w-28 px-3',
                  project:   'px-3 w-48',
                  role:      'w-44 px-3',
                  hours:     'w-24 px-3',
                  startDate: 'w-40 px-3',
                  status:    'w-32 px-3',
                };
                return (
                  <SortableHead
                    key={key}
                    field={colDef.sortField}
                    label={colDef.label}
                    currentField={sortField}
                    currentDir={sortDir}
                    onSort={handleSort}
                    className={colClassMap[key]}
                  />
                );
              })}

              {/* Actions — always visible */}
              <TableHead className={cn('w-52 px-3', TABLE_HEAD_CLASS)}>
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {paginated.length === 0 ? (
              <EmptyState filtered={isFiltered} onClear={handleClearFilters} colSpan={rrVisibleCount + 2} />
            ) : (
              paginated.map((request, rowIndex) => {
                const statusCfg   = STATUS_CONFIG[request.status];
                const priorityCfg = PRIORITY_CONFIG[request.priority];
                const isExpanded  = expandedRow === request.id;
                const isDimmed    = RR_TERMINAL_STATUSES.has(request.status);
                const receivedAgo = formatDistanceToNow(parseISO(request.receivedAt), { addSuffix: true });

                return (
                  <React.Fragment key={request.id}>
                    {/* ── Main row ── */}
                    <TableRow
                      className={cn(
                        'cursor-pointer transition-colors',
                        isExpanded && 'bg-muted/30 border-b-0',
                        isDimmed && 'opacity-50',
                      )}
                      style={isExpanded ? undefined : alternatingRowStyle(rowIndex)}
                      {...(isExpanded ? {} : alternatingRowHoverHandlers(rowIndex))}
                      onClick={() => toggleRow(request.id)}
                    >
                      {/* Expand chevron */}
                      <TableCell className="px-3 py-3 w-8">
                        <ChevronRight
                          className={cn(
                            'h-4 w-4 text-muted-foreground transition-transform duration-200 shrink-0',
                            isExpanded && 'rotate-90',
                          )}
                        />
                      </TableCell>

                      {/* Render cells in column order, respecting visibility */}
                      {rrColumnOrder.filter(k => rrVisibleColumns.has(k)).map(key => {
                        if (key === 'priority') return (
                          <TableCell key={key} className="px-3 py-3">
                            <div className="flex items-center gap-2">
                              <span
                                className={cn('h-2 w-2 rounded-full shrink-0', priorityCfg.dotAccent)}
                                style={{ backgroundColor: 'var(--accent-border)' }}
                              />
                              <Badge
                                variant="accent"
                                accent={priorityCfg.badgeAccent}
                                className="text-[10px] px-1.5 py-0 h-4 capitalize font-semibold"
                              >
                                {priorityCfg.label}
                              </Badge>
                            </div>
                          </TableCell>
                        );
                        if (key === 'project') return (
                          <TableCell key={key} className="px-3 py-3">
                            <div className="flex flex-col gap-0.5 min-w-0">
                              <span className="text-sm font-semibold leading-tight truncate max-w-[160px]">
                                {request.projectName}
                              </span>
                              {request.projectCode && (
                                <span className="text-xs text-muted-foreground truncate max-w-[200px]">
                                  {request.projectCode}
                                </span>
                              )}
                            </div>
                          </TableCell>
                        );
                        if (key === 'role') return (
                          <TableCell key={key} className="px-3 py-3">
                            <span className="text-sm text-foreground/80 truncate block max-w-[160px]">
                              {request.role}
                            </span>
                          </TableCell>
                        );
                        if (key === 'hours') {
                          if (manualHoursPending) {
                            return (
                              <TableCell key={key} className="px-3 py-3">
                                <Skeleton className="h-4 w-16" />
                              </TableCell>
                            );
                          }
                          const alloc = request.allocatedHours ?? 0;
                          const original = request.originalHoursRequested ?? request.totalHoursRequested;
                          const fullyAllocated = request.totalHoursRequested <= 0 && alloc > 0;
                          return (
                          <TableCell key={key} className="px-3 py-3">
                            <TooltipProvider delayDuration={200}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="text-sm font-bold tabular-nums cursor-default">
                                    {fullyAllocated ? (
                                      <>
                                        {alloc}h
                                        <span className="ml-1 text-xs font-normal text-muted-foreground">booked</span>
                                      </>
                                    ) : (
                                      <>
                                        {request.totalHoursRequested}h
                                        {alloc > 0 && (
                                          <span className="ml-1 text-xs font-normal text-muted-foreground">
                                            of {original}h
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="text-xs">
                                  {fullyAllocated
                                    ? `${hoursLabel(alloc)} booked — nothing outstanding`
                                    : alloc > 0
                                      ? `${hoursLabel(request.totalHoursRequested)} outstanding — ${alloc}h already booked`
                                      : hoursLabel(request.totalHoursRequested)}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </TableCell>
                          );
                        }
                        if (key === 'startDate') {
                          const proximity = RR_PROXIMITY_STATUSES.has(request.status)
                            ? getStartDateProximity(request.requestedStartDate)
                            : null;
                          return (
                          <TableCell key={key} className="px-3 py-3">
                            <div className="flex flex-col gap-0.5 min-w-0">
                              <div className="flex items-center gap-1.5 text-sm font-semibold leading-tight text-foreground">
                                <CalendarDays className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                {fmtDate(request.requestedStartDate)}
                              </div>
                              {proximity && (
                                <span
                                  className={cn(
                                    'text-[11px] leading-tight',
                                    proximity.passed ? 'text-destructive font-medium' : 'text-muted-foreground',
                                  )}
                                >
                                  {proximity.label}
                                </span>
                              )}
                            </div>
                          </TableCell>
                          );
                        }
                        if (key === 'status') return (
                          <TableCell key={key} className="px-3 py-3">
                            <Badge
                              variant="accent"
                              accent={statusCfg.accent}
                              className="gap-1 text-[10px] h-5 px-1.5"
                            >
                              {statusCfg.icon}
                              {statusCfg.label}
                            </Badge>
                          </TableCell>
                        );
                        return null;
                      })}

                      {/* Quick actions */}
                      <TableCell
                        className="px-3 py-3"
                        onClick={e => e.stopPropagation()}
                      >
                        <div className="flex items-center gap-1.5 whitespace-nowrap">
                          {request.status === 'pending' && (
                            <Button
                              size="sm"
                              className={RR_ROW_ACTION_CLASS}
                              onClick={() => handleGeneratePlan(request)}
                            >
                              Generate
                            </Button>
                          )}
                          {request.status === 'plan-ready' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className={RR_ROW_ACTION_CLASS}
                              onClick={() => handleViewPlan(request)}
                            >
                              View Plan
                            </Button>
                          )}
                          {request.status === 'accepted' && (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className={RR_ROW_ACTION_CLASS}
                              onClick={() => handleViewPlan(request)}
                            >
                              <CheckCircle2 className="h-4 w-4" />
                              View
                            </Button>
                          )}
                          {request.status === 'partially-allocated' && (
                            <>
                              <Button
                                size="sm"
                                className={RR_ROW_ACTION_CLASS}
                                onClick={() => handleViewPlan(request)}
                              >
                                Plan remaining
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className={RR_ROW_ACTION_CLASS}
                                onClick={() => handleViewBookings(request)}
                              >
                                <Eye className="h-4 w-4" />
                                View
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>

                    {/* ── Expanded detail row ── */}
                    {isExpanded && (
                      <ExpandedDetail
                        request={request}
                        onViewPlan={() => handleViewPlan(request)}
                        onDecline={() => handleDecline(request.id)}
                        colSpan={rrVisibleCount + 2}
                      />
                    )}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* ── Pagination ── */}
      <TablePagination
        page={page}
        pageSize={pageSize}
        totalItems={filtered.length}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        itemLabel="requests"
      />

      {/* ── Table footer summary ── */}
      {filtered.length > 0 && (
        <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
          {manualHoursPending ? (
            <Skeleton className="h-4 w-full" />
          ) : (
            <>
              <span>
                {filtered.filter(r => r.status === 'pending').length} pending ·{' '}
                {filtered.filter(r => r.status === 'plan-ready').length} plan ready ·{' '}
                {filtered.filter(r => r.status === 'partially-allocated').length} partially allocated ·{' '}
                {filtered.filter(r => r.status === 'accepted').length} accepted
              </span>
              <span className="tabular-nums">
                {filtered.reduce((s, r) => s + r.totalHoursRequested, 0)}h total hours
              </span>
            </>
          )}
        </div>
      )}

      {/* ── Plan Drawer ── */}
      <ResourceRequestPlanDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        request={activeRequest}
        resources={resources}
        existingBookings={existingBookings}
        scenarios={activeScenarios}
        isGenerating={isGenerating}
        acceptedBookings={acceptedBookings}
        acceptedBookingsLoading={acceptedBookingsLoading}
        viewMode={drawerView}
        onPlanRemaining={activeRequest ? () => handleViewPlan(activeRequest) : undefined}
        onAccept={handleAccept}
        onPreviewOnCalendar={onPreviewOnCalendar}
      />

      {/* ── Configure Columns Sheet ── */}
      <Sheet open={rrConfigOpen} onOpenChange={setRrConfigOpen}>
        <SheetContent side="right" className="w-[440px] sm:max-w-[440px] flex flex-col p-0 gap-0">
          {/* Header */}
          <SheetHeader className="px-6 pt-6 pb-5 border-b shrink-0">
            <div className="flex items-center gap-3 pr-8">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 shrink-0">
                <Settings2 className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <SheetTitle className="text-base font-semibold leading-tight">
                  Customise Columns
                </SheetTitle>
                <SheetDescription className="text-xs mt-0.5 leading-relaxed">
                  Toggle columns on or off, then drag to reorder them.
                </SheetDescription>
              </div>
            </div>

            {/* Visible count + reset */}
            <div className="flex items-center justify-between mt-4">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold tabular-nums shrink-0">
                  {rrVisibleCount}
                </div>
                <span className="text-sm text-muted-foreground">
                  of {RR_ALL_COLUMNS.length} columns showing
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 gap-1.5 text-xs text-muted-foreground hover:text-foreground"
                onClick={rrResetColumns}
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Reset defaults
              </Button>
            </div>
          </SheetHeader>

          {/* Drag hint */}
          <div className="px-6 py-3 bg-muted/30 border-b shrink-0">
            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
              <GripVertical className="h-3.5 w-3.5 shrink-0" />
              Drag the handle on any row to reorder columns
            </p>
          </div>

          {/* Column list */}
          <ScrollArea className="flex-1 min-h-0">
            <div className="px-4 py-4">
              <TooltipProvider delayDuration={300}>
                <DndContext
                  sensors={rrDndSensors}
                  collisionDetection={closestCenter}
                  onDragEnd={rrHandleDragEnd}
                >
                  <SortableContext items={rrColumnOrder} strategy={verticalListSortingStrategy}>
                    <div className="space-y-2">
                      {rrColumnOrder.map(key => {
                        const colDef = RR_ALL_COLUMNS.find(c => c.key === key)!;
                        return (
                          <RRSortableColumnRow
                            key={key}
                            colDef={colDef}
                            isVisible={rrVisibleColumns.has(key)}
                            onToggle={rrToggleColumn}
                          />
                        );
                      })}
                    </div>
                  </SortableContext>
                </DndContext>
              </TooltipProvider>
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>
    </div>
  );
}
